/**
 * @file Components/Tours/CategoryNav.jsx
 * @description Horizontal icon-based category navigation bar for filtering tours.
 * MakeMyTrip-style premium category selector with animated indicators.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Compass, GraduationCap, Mountain, Crown, Heart, Briefcase, Globe } from 'lucide-react';

const categories = [
  { key: 'all', label: 'All Tours', icon: Globe, color: 'from-blue-500 to-indigo-600' },
  { key: 'customized', label: 'Customized', icon: Compass, color: 'from-purple-500 to-pink-500' },
  { key: 'educational', label: 'Educational', icon: GraduationCap, color: 'from-green-500 to-teal-500' },
  { key: 'adventurous', label: 'Adventurous', icon: Mountain, color: 'from-orange-500 to-red-500' },
  { key: 'luxury', label: 'Luxury', icon: Crown, color: 'from-amber-500 to-yellow-500' },
  { key: 'family', label: 'Family', icon: Heart, color: 'from-pink-500 to-rose-500' },
  { key: 'corporate', label: 'Corporate', icon: Briefcase, color: 'from-slate-500 to-gray-600' },
];

const CategoryNav = ({ activeCategory = 'all', onCategoryChange, categoryCounts = {} }) => {
  return (
    <div className="w-full bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {categories.map((cat) => {
            const isActive = activeCategory === cat.key;
            const Icon = cat.icon;
            const count = cat.key === 'all'
              ? Object.values(categoryCounts).reduce((s, c) => s + c, 0)
              : (categoryCounts[cat.key] || 0);

            return (
              <motion.button
                key={cat.key}
                onClick={() => onCategoryChange(cat.key)}
                className={`relative flex flex-col items-center min-w-[90px] px-4 py-2 rounded-xl transition-all duration-200 group ${
                  isActive
                    ? 'bg-gradient-to-br text-white shadow-lg scale-105 ' + cat.color
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                }`}
                whileHover={{ scale: isActive ? 1.05 : 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Icon size={22} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'} />
                <span className={`text-xs font-semibold mt-1 whitespace-nowrap ${isActive ? 'text-white' : ''}`}>
                  {cat.label}
                </span>
                {count > 0 && (
                  <span className={`text-[10px] mt-0.5 ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                    {count} tour{count !== 1 ? 's' : ''}
                  </span>
                )}
                {isActive && (
                  <motion.div
                    layoutId="categoryIndicator"
                    className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-1 bg-current rounded-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { categories };
export default CategoryNav;
